import { FC } from "../index"
import { useState, useEffect } from "react"
import { isEmpty } from "lodash"

// Import hooks and utilities
import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"
import { mapArticleTeaser, mapWishlistData } from "../../helpers/data-mappers"
import {
  useArticlesSolrData,
  useGlobalAdsSolrData,
  useStaffData,
  useTripsData,
  useArticleCategoryData,
} from "../../local/hooks"

// Import paragraph components that can be rendered dynamically
import ContactCTA from "../ContactCTA/ContactCTA"
import TextOverview from "../TextOverview/TextOverview"
import ParallaxMedia from "../ParallaxMedia/ParallaxMedia"
import BenefitsList from "../BenefitsList/BenefitsList"
import TripStyles from "../TripStyles/TripStyles"
import SuggestedTrips from "../SuggestedTrips/SuggestedTrips"
import ContentHighlight from "../ContentHighlight/ContentHighlight"
import StackedCTA from "../StackedCTA/StackedCTA"
import VisualMediaContent from "../VisualMediaContent/VisualMediaContent"
import ExploreList from "../ExploreList/ExploreList"
import Facts from "../Facts/Facts"
import BannerCTA from "../BannerCTA/BannerCTA"
import Teaser from "../TeaserGrid/TeaserGrid"
import Text from "../Text/Text"
import SectionSwitcher from "../SectionSwitcher/SectionSwitcher"
import LocationBanners from "../LocationBanners/LocationBanners"
import Hero from "../Hero/Hero"
import ImageOverview from "../ImageOverview/ImageOverview"
import TrustPilotWidget from "../TrustPilotWidget/TrustPilotWidget"
import PerksList from "../PerksList/PerksList"
import Slideshow from "../Slideshow/Slideshow"
import BlogSubNavigation from "../BlogSubNavigation/BlogSubNavigation"
import TrendingBlogPosts from "../TrendingBlogPosts/TrendingBlogPosts"
import BlogCategorySection from "../BlogCategorySection/BlogCategorySection"

interface FromLibraryProps {
  id?: string
  paragraph?: {
    id: string
    label: string
    paragraphs: {
      id: string
      type: string
      [key: string]: any
    }
  }
}

const FromLibrary: FC<FromLibraryProps> = ({ id, paragraph }) => {
  // State for fetched data
  const [contactCtaStaff, setContactCtaStaff] = useState(null)
  const [globalAds, setGlobalAds] = useState(null)
  const [articleTeasers, setArticleTeasers] = useState(null)
  const [suggestedTrips, setSuggestedTrips] = useState(null)
  const [staffSlideshowData, setStaffSlideshowData] = useState(null)
  const [trendingArticles, setTrendingArticles] = useState(null)
  const [articleCategories, setArticleCategories] = useState(null)

  // Hooks and selectors
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedArticles = wishlist?.article && wishlist?.article.map((article) => article.nid)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip) => trip.nid)
  const userCountry = useSelector((state) => state.settings.country)
  
  // Data fetching hooks
  const { getRandomStaff, getStaffData } = useStaffData()
  const { getHomepageGenericGlobalAds } = useGlobalAdsSolrData()
  const { getArticles, getTrendingArticles } = useArticlesSolrData()
  const { getTripsData } = useTripsData()
  const { getArticleCategoryData } = useArticleCategoryData()

  // Get component info
  const component = paragraph?.paragraphs
  const componentType = component?.type

  // Fetch data based on component type
  useEffect(() => {
    if (!componentType) return

    if (componentType === "contact_cta") {
      getRandomStaff().then((data) => {
        setContactCtaStaff(data)
      })
    }

    if (componentType === "global_ad") {
      getHomepageGenericGlobalAds().then((data) => {
        setGlobalAds(data)
      })
    }

    if (componentType === "teaser_3up") {
      getArticles(component).then((data) => {
        const filteredData = mapWishlistData(likedArticles, data)
        setArticleTeasers(filteredData)
      })
    }

    if (componentType === "suggested_trips") {
      getTripsData(null, component).then((data) => {
        const filteredData = mapWishlistData(likedTrips, data)
        setSuggestedTrips({
          items: filteredData,
        })
      })
    }

    if (componentType === "staff_slideshow") {
      getStaffData(null).then((data) => {
        setStaffSlideshowData(data)
      })
    }

    if (componentType === "blog_sub_nav") {
      getArticleCategoryData().then((data) => {
        setArticleCategories(data)
      })
    }

    if (componentType === "trending") {
      getTrendingArticles().then((data) => {
        const filteredData = mapWishlistData(likedArticles, data)
        setTrendingArticles({
          title: "Trending",
          uri: "#",
          items: filteredData,
        })
      })
    }
  }, [componentType, likedArticles, likedTrips, userCountry, component])

  // Early return after all hooks
  if (!paragraph?.paragraphs) {
    return null
  }

  // Wishlist handlers
  const addToWishList = (selectedEntity, type) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articleTeasers, selectedEntity, true)
      setArticleTeasers(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips?.items, selectedEntity, true)
      setSuggestedTrips({
        items: filteredState,
      })
    }
    if (type === "trending") {
      const filteredState = mapArticleTeaser(trendingArticles?.items, selectedEntity, true)
      setTrendingArticles({
        ...trendingArticles,
        items: filteredState,
      })
    }
    dispatch(
      addNode({ type: selectedEntity.contentType, entity: selectedEntity, idPropertyKey: "nid" })
    )
  }

  const removeFromWishList = (selectedEntity, type) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articleTeasers, selectedEntity, false)
      setArticleTeasers(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips?.items, selectedEntity, false)
      setSuggestedTrips({
        items: filteredState,
      })
    }
    if (type === "trending") {
      const filteredState = mapArticleTeaser(trendingArticles?.items, selectedEntity, false)
      setTrendingArticles({
        ...trendingArticles,
        items: filteredState,
      })
    }
    dispatch(removeNode({ type: selectedEntity.contentType, findPredicate: selectedEntity }))
  }

  // Dynamically render the appropriate component based on type
  switch (componentType) {
    case "contact_cta":
      return (
        <ContactCTA
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          description={component.description}
          link={component.link}
          image={component.image}
          staff={contactCtaStaff}
        />
      )
    
    case "text_overview":
      return (
        <TextOverview
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          layout={component.layout || "full"}
          description={component.description}
          link={component.link}
        />
      )
    
    case "parallax_media":
      return (
        <ParallaxMedia
          key={component.id}
          image_1={component.image_1}
          image_2={component.image_2}
          image_3={component.image_3}
          video={component.video}
        />
      )
    
    case "benefits_list":
      return (
        <BenefitsList
          key={component.id}
          items={component.items}
        />
      )
    
    case "trip_styles":
      return (
        <TripStyles
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          layout={component.layout}
          items={component.travel_items}
        />
      )
    
    case "suggested_trips":
      return (
        <>
          {suggestedTrips?.items?.length && (
            <SuggestedTrips
              key={component.id}
              title={component.title}
              subtitle={component.subtitle}
              items={suggestedTrips.items}
              link={component.link}
              onLike={addToWishList}
              onUnlike={removeFromWishList}
            />
          )}
        </>
      )
    
    case "content_highlight":
      return (
        <ContentHighlight
          key={component.id}
          color={component.background_color}
          title={component.title}
          subtitle={component.subtitle}
          description={component.description}
          image={component.image}
          link={component.link}
        />
      )
    
    case "stacked_cta":
      return (
        <StackedCTA
          key={component.id}
          direction={component.direction}
          items={component.items}
        />
      )
    
    case "visual_media_content":
      return (
        <VisualMediaContent
          key={component.id}
          images={component.media}
        />
      )
    
    case "explore_list":
      return (
        <ExploreList
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          description={component.description}
          items={component.items}
        />
      )
    
    case "facts_overview":
      return (
        <Facts
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          description={component.description}
          items={[
            { title: "Capital", value: component.capital },
            { title: "Currency", value: component.currency },
            { title: "Official Languages", value: component.official_languages },
            { title: "Population", value: component.population },
          ].filter((item) => item.value)}
        />
      )
    
    case "banner_cta":
      return (
        <BannerCTA
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          image={component.image}
          link={component.link}
        />
      )

    case "hero_s1":
      return (
        <Hero
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          media={component.images || []}
          video={component.video}
          path={component.path}
        />
      )

    case "image_overview":
      return (
        <ImageOverview
          key={component.id}
          title={component.title}
          description={component.description}
          image={component.image}
          link={component.link}
        />
      )

    case "trustpilot_widget":
      return (
        <TrustPilotWidget
          key={component.id}
          variant="preFooter"
        />
      )

    case "perks_list":
      return (
        <PerksList
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          items={component.items}
        />
      )

    case "staff_slideshow":
    case "places_slideshow":
      return (
        <Slideshow
          key={component.id}
          title={component.title}
          subtitle={component.subtitle}
          items={componentType === "staff_slideshow" ? staffSlideshowData : component.items || component.places || []}
        />
      )
    
    case "global_ad":
    case "run_of_site_ad":
    case "trip_level_ad":
      return (
        <>
          {!isEmpty(globalAds) && (
            <LocationBanners
              key={component.id}
              items={globalAds}
            />
          )}
        </>
      )
    
    case "teaser_3up":
      return (
        <Teaser
          key={component.id}
          title={component.title}
          subtitle={component.subtitle || ""}
          link={component.link}
          items={articleTeasers || component.article_list_override || []}
          onLike={addToWishList}
          onUnlike={removeFromWishList}
        />
      )
    
    case "text":
      return (
        <Text
          key={component.id}
          title={component.title}
          text={component.text}
        />
      )
    
    case "section_switcher":
      return (
        <SectionSwitcher
          key={component.id}
          items={component.items}
        />
      )

    case "blog_sub_nav":
      return (
        <BlogSubNavigation
          key={component.id}
          title={component.title || "Globetrotting"}
          items={articleCategories || component.items || []}
        />
      )

    case "trending":
      return (
        <>
          {trendingArticles && (
            <TrendingBlogPosts
              key={component.id}
              {...trendingArticles}
              onLike={(entity) => addToWishList(entity, "trending")}
              onUnlike={(entity) => removeFromWishList(entity, "trending")}
              componentType="trending"
            />
          )}
        </>
      )

    case "blog_category_section":
      return (
        <BlogCategorySection
          key={component.id}
          title={component.title}
          link={component.link}
          variant={component.variant || "threeCards"}
          backgroundColor={component.backgroundColor || "white"}
          items={component.items || []}
          onLike={(entity) => addToWishList(entity, "articles_by_category")}
          onUnlike={(entity) => removeFromWishList(entity, "articles_by_category")}
          componentType="articles_by_category"
        />
      )
    
    default:
      console.warn(`FromLibrary: Unsupported component type "${componentType}"`)
      return null
  }
}

export default FromLibrary 